import { API_URL } from "./auth-shared";
import { rewriteUploadsUrl } from "./rewrite-uploads";

// Espeja el shape del endpoint /v1/presentations/public.
export type SaleType = "MENUDEO" | "MAYOREO";

export interface PresentationVariant {
  // Egg-type al que aplica la variante. NULL = foto global (empaques
  // opacos, una sola foto válida para cualquier egg-type).
  eggTypeId: string | null;
  photoUrl: string;
}

// Geometría de huecos sobre la foto aérea de una presentación. La
// autorea el admin en el panel y la consume el builder de cartón
// personalizado — todo en porcentajes, resolución-agnóstico. Espeja
// la interfaz `SlotLayout` del API.
export interface SlotPosition {
  left: number;
  top: number;
}
export interface SlotLayout {
  // Aspect ratio (ancho / alto) de la foto aérea.
  aspect: number;
  // Diámetro del hueco, % del ancho del lienzo.
  slotSize: number;
  // Lado de la caja del huevo realista, % del hueco.
  eggSize: number;
  // Desplazamiento vertical del huevo, % del alto del hueco.
  eggDrop: number;
  // Centro de cada hueco; longitud == capacity.
  slots: SlotPosition[];
}

export interface PublicPresentation {
  id: string;
  name: string;
  capacity: number;
  saleType: SaleType | string;
  // Variantes de foto vinculadas a egg-types específicos. El web cliente
  // matchea por eggTypeId contra el egg-type que el usuario está viendo.
  // Si no hay match exacto, cae al variant con eggTypeId=null o al
  // primero disponible.
  variants: PresentationVariant[];
  /**
   * Foto cenital del empaque (el cartón visto desde arriba) sobre la
   * que el builder de cartón personalizado coloca un huevo por hueco.
   * Null cuando el admin no la configuró — el builder cae a su cartón
   * genérico.
   */
  aerialPhotoUrl?: string | null;
  /**
   * Geometría de huecos sobre `aerialPhotoUrl`. Null sin configurar.
   */
  slotLayout?: SlotLayout | null;
  /**
   * Precio final por huevo en este empaque, indexado por egg-type. Lo
   * calcula el backend a partir de costo + empaque + etiqueta + margen,
   * gross-up por ISR/Stripe%. Si una egg-type no aparece en este mapa,
   * su precio no es computable y la UI cae al `unitPrice` "desnudo"
   * que viene en cada egg-type (sin empaque ni etiqueta).
   *
   * Cuando hay descuento APTO activo este valor YA está rebajado.
   * Para mostrar el precio tachado se usa `originalPricePerUnitByEgg`.
   */
  pricePerUnitByEgg?: Record<string, number>;
  /**
   * Pre-discount mirror of `pricePerUnitByEgg`. Populated for every
   * egg-type that ends up in `pricePerUnitByEgg` (same keys), with the
   * value the customer would pay if APTO discount were not in play.
   * Identical to the discounted map for FRESCO/null egg-types — the
   * UI checks `aptoDiscountByEgg` to know whether to render the strike.
   * Optional for backward compatibility with older API responses.
   */
  originalPricePerUnitByEgg?: Record<string, number>;
  /**
   * Egg-types with an active APTO discount on this presentation. Map
   * is empty (or absent) when nothing is on promo. `percentage` is a
   * fraction in (0, 1) — e.g. 0.15 = 15% off. Presence in this map is
   * the truthy "is on promo" signal for the UI.
   */
  aptoDiscountByEgg?: Record<
    string,
    { percentage: number; freshnessState: "APTO" }
  >;
  /** Costo del cartón vacío (MXN). Se suma al subtotal por línea. */
  packagingCost?: number;
  /** Costo de la etiqueta impresa (MXN). 0 si no aplica. */
  labelCost?: number;
}

// Fail-open: si el API no responde, una "Docena" sin foto para que el
// catálogo siga vendible. Sin esto el web reventaría en una caída del API.
const FALLBACK_PRESENTATIONS: PublicPresentation[] = [
  {
    id: "fallback-12",
    name: "Docena",
    capacity: 12,
    saleType: "MENUDEO",
    variants: [],
  },
];

export async function fetchPublicPresentations(): Promise<PublicPresentation[]> {
  try {
    const res = await fetch(`${API_URL}/presentations/public`, {
      // Sin caché: las altas/bajas de presentaciones (y la foto aérea
      // + slotLayout) cambian desde el panel y el cliente debe verlas
      // al instante en el catálogo y en /customize. La caché de ue-web
      // NO es invalidable desde el panel — son apps Next separadas con
      // cachés independientes — así que cualquier `revalidate` dejaba
      // la lista vieja hasta 10 min.
      cache: "no-store",
    });
    if (!res.ok) return FALLBACK_PRESENTATIONS;
    const data = (await res.json()) as PublicPresentation[];
    if (data.length === 0) return FALLBACK_PRESENTATIONS;
    // Reescribimos los photoUrl de cada variante (y la foto aérea) al
    // proxy local — el browser no puede llegar al api-dev directo (CF
    // Access).
    return data.map((p) => ({
      ...p,
      variants: (p.variants ?? []).map((v) => ({
        eggTypeId: v.eggTypeId,
        photoUrl: rewriteUploadsUrl(v.photoUrl) ?? v.photoUrl,
      })),
      aerialPhotoUrl: rewriteUploadsUrl(p.aerialPhotoUrl ?? null),
      slotLayout: p.slotLayout ?? null,
    }));
  } catch {
    return FALLBACK_PRESENTATIONS;
  }
}

/**
 * Effective per-egg price in a specific presentation. Prefers the
 * server-computed value in `pricePerUnitByEgg` (which folds in
 * cost + packaging + label + margin grossed up by ISR/Stripe%). Falls
 * back to the egg's bare `unitPrice` (cost + margin + fees, no
 * packaging) when the new map doesn't cover this egg-type — that
 * happens when costPerUnit is 0 or the pricing config is unset.
 */
export function pickEggPriceForPresentation(
  presentation: Pick<PublicPresentation, "pricePerUnitByEgg">,
  eggType: { id: string; unitPrice: number },
): number {
  const fromMap = presentation.pricePerUnitByEgg?.[eggType.id];
  if (typeof fromMap === "number" && Number.isFinite(fromMap) && fromMap > 0) {
    return fromMap;
  }
  return Number(eggType.unitPrice) || 0;
}

/**
 * Headline per-egg price across the catalog — picks the value from
 * the smallest-capacity presentation that lists this egg-type. Used
 * on the product page header (the catalog grid no longer shows a
 * per-piece price; pricing surfaces only when the user picks a
 * presentation).
 */
export function representativeEggPrice(
  eggType: { id: string; unitPrice: number },
  presentations: PublicPresentation[],
): number {
  const sorted = [...presentations].sort((a, b) => a.capacity - b.capacity);
  for (const p of sorted) {
    const v = p.pricePerUnitByEgg?.[eggType.id];
    if (typeof v === "number" && Number.isFinite(v) && v > 0) return v;
  }
  return Number(eggType.unitPrice) || 0;
}

// Devuelve la foto de la presentación que mejor matchee con el egg-type
// que el usuario está viendo. Orden de prioridad:
//   1. Variante exacta del egg-type (cartón de 30 transparente con foto
//      específica de gallina blanca, codorniz, pato, etc.).
//   2. Variante global (eggTypeId=null) — foto del empaque opaco que
//      sirve para cualquier egg-type.
//   3. Primera variante disponible — fallback final.
//   4. null si la presentación no tiene ninguna variante.
export function pickPresentationPhoto(
  presentation: Pick<PublicPresentation, "variants">,
  eggTypeId: string | null | undefined,
): string | null {
  if (!presentation.variants || presentation.variants.length === 0) {
    return null;
  }
  if (eggTypeId) {
    const exact = presentation.variants.find(
      (v) => v.eggTypeId === eggTypeId,
    );
    if (exact) return exact.photoUrl;
  }
  const global = presentation.variants.find((v) => v.eggTypeId === null);
  if (global) return global.photoUrl;
  return presentation.variants[0]?.photoUrl ?? null;
}
